import { Link, useParams } from "react-router-dom";
import { useLang } from "../i18n";
import { useSolutions, useSiteImages } from "../content/useContent";
import { IconByName } from "../components/IconByName";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FinalCTA } from "../components/FinalCTA";
import { Check, ArrowRight } from "lucide-react";
import NotFound from "./NotFound";

const HERO_KEYS = {
  "fibre-optique": "fiberWork",
  "internet-fixe-sans-fil": "towerSunset",
  "internet-satellite": "satellite",
  "ip-transit-dia": "dataCenter",
  "sd-wan-mpls": "teamMeeting",
  "securite-videosurveillance": "mastDusk",
  "genie-civil-fibre": "engineerPanel",
  "reseaux-prives-fibre-noire": "dataCenter",
};

const SolutionDetail = () => {
  const { lang, t, p } = useLang();
  const { slug } = useParams();
  const solutions = useSolutions();
  const IMG = useSiteImages();
  const sol = solutions.find((s) => s.slug === slug);
  if (!sol) return <NotFound />;
  const c = sol[lang];
  const sc = t.solutionCommon;
  const related = solutions.filter((s) => s.slug !== slug).slice(0, 3);

  const faqLd = c.faq?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: c.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  } : undefined;

  return (
    <div data-testid={`solution-page-${slug}`}>
      <Seo title={c.title} description={c.short} path={`solutions/${slug}`} jsonLd={faqLd} />
      <PageHero eyebrow={t.nav.solutions.toUpperCase()} title={c.title} sub={c.short}
        image={IMG[HERO_KEYS[slug]]} crumbs={[{ label: t.nav.solutions, to: p("solutions") }, { label: c.title }]} />

      <section className="bg-[#F5F5F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-14">
            <Reveal>
              <h2 className="text-xl sm:text-2xl font-bold text-[#101214]">{sc.outcome}</h2>
              <p className="mt-4 text-base sm:text-lg font-bold text-[#FF6600] leading-relaxed">{c.outcome}</p>
            </Reveal>
            <Reveal>
              <h2 className="text-xl sm:text-2xl font-bold text-[#101214]">{sc.overview}</h2>
              <p className="mt-4 text-base text-[#101214]/70 leading-relaxed">{c.overview}</p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {c.capabilities.map((cap) => (
                  <li key={cap.slice(0, 30)} className="flex items-start gap-2.5 text-sm text-[#101214]/75">
                    <Check size={16} className="text-[#FF6600] shrink-0 mt-0.5" />{cap}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal>
              <h2 className="text-xl sm:text-2xl font-bold text-[#101214]">{sc.useCases}</h2>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {c.useCases.map((u) => (
                  <span key={u} className="bg-white border border-[#101214]/12 text-sm text-[#101214]/75 rounded-full px-4 py-2">{u}</span>
                ))}
              </div>
            </Reveal>
            <Reveal>
              <h2 className="text-xl sm:text-2xl font-bold text-[#101214]">{sc.process}</h2>
              <ol className="mt-6 space-y-4">
                {sc.processSteps.map((step, i) => (
                  <li key={step} className="flex gap-4 items-start">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-[#FF6600] text-[#08090A] font-black text-sm flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm sm:text-base text-[#101214]/75 pt-1">{step}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-6 text-xs text-[#101214]/45 italic">{sc.availability}</p>
            </Reveal>
            {c.faq?.length > 0 && (
              <Reveal>
                <h2 className="text-xl sm:text-2xl font-bold text-[#101214]">{sc.faq}</h2>
                <div className="mt-6 space-y-4">
                  {c.faq.map((f) => (
                    <details key={f.q} className="group bg-white border border-[#101214]/10 rounded-xl p-5 open:border-[#FF6600]/40">
                      <summary className="cursor-pointer font-bold text-sm sm:text-base text-[#101214] list-none flex justify-between items-center gap-4">
                        {f.q}<span className="text-[#FF6600] group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                      </summary>
                      <p className="mt-3 text-sm text-[#101214]/65 leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Reveal className="bg-[#101214] rounded-2xl p-7">
                <h3 className="text-white font-bold text-lg">{sc.segments}</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {c.segments.map((s) => <span key={s} className="text-xs font-bold text-white/70 border border-white/15 rounded-full px-3.5 py-1.5">{s}</span>)}
                </div>
                <h3 className="mt-7 text-white font-bold text-lg">{sc.benefits}</h3>
                <ul className="mt-4 space-y-2.5">
                  {c.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-white/70">
                      <Check size={15} className="text-[#FF9900] shrink-0 mt-0.5" />{b}
                    </li>
                  ))}
                </ul>
                <Link to={p("demander-un-devis")} data-testid="solution-quote-cta"
                  className="mt-7 flex items-center justify-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] font-black rounded-full px-6 py-3.5 text-sm transition-colors w-full">
                  {c.ctaLabel || sc.cta} <ArrowRight size={16} />
                </Link>
                <Link to={p("verifier-la-couverture")} data-testid="solution-coverage-cta"
                  className="mt-3 flex items-center justify-center gap-2 border border-white/20 hover:border-[#FF6600] text-white font-bold rounded-full px-6 py-3 text-sm transition-colors w-full">
                  {t.nav.coverage}
                </Link>
              </Reveal>
              <Reveal className="bg-white rounded-2xl border border-[#101214]/10 p-7">
                <h3 className="font-bold text-[#101214]">{sc.related}</h3>
                <ul className="mt-4 space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={p(`solutions/${r.slug}`)} className="group flex items-center gap-3 text-sm font-bold text-[#101214]/75 hover:text-[#FF6600] transition-colors">
                        <IconByName name={r.icon} size={18} className="text-[#FF6600]" />{r[lang].title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </aside>
        </div>
      </section>
      <FinalCTA />
    </div>
  );
};

export default SolutionDetail;
